/**
 * テキストレイアウト計算サービス
 * C#のTextLayoutServiceの機能をTypeScript側に移行
 */

import {
    EngraveSettings,
    TextLayout,
    TextPosition,
    StrapDimensions,
    ValidationResult,
    TextBlockDimensions,
    TextAlignment,
    HorizontalAlignment,
    VerticalAlignment
} from '../types';

/**
 * テキストレイアウト計算を行うクラス
 */
export class TextLayoutService {
    private ctx: CanvasRenderingContext2D;

    /**
     * コンストラクタ
     * @param ctx Canvas 2Dコンテキスト（テキスト幅計測に使用）
     */
    constructor(ctx: CanvasRenderingContext2D) {
        this.ctx = ctx;
    }

    /**
     * StrapDimensionsからmm→px変換係数を計算
     * ズームレベルに応じて自動的にスケーリングされる
     * @param strap ストラップ寸法
     * @returns mm→px変換係数
     */
    private getMmToPx(strap: StrapDimensions): number {
        return strap.canvasWidth / strap.widthMm;
    }

    /**
     * StrapDimensionsからpt→px変換係数を計算
     * ズームレベルに応じて自動的にスケーリングされる
     * @param strap ストラップ寸法
     * @returns pt→px変換係数
     */
    private getPtToPx(strap: StrapDimensions): number {
        // 基本のpt→px変換係数（96 DPI）
        const basePtToPx = 96 / 72;
        // 現在のDPIを計算（ズーム倍率を反映）
        const currentDpi = this.getMmToPx(strap) * 25.4;
        // ズーム倍率を適用
        return basePtToPx * (currentDpi / 96);
    }

    /**
     * 彫刻設定からテキストレイアウトを計算
     * @param settings 彫刻設定
     * @param strap ストラップ寸法
     * @returns 計算されたテキストレイアウト
     */
    calculateLayout(settings: EngraveSettings, strap: StrapDimensions): TextLayout {
        // 1. テキストを行に分割
        const lines = this.splitIntoLines(settings.text);

        // 2. フォントスタイルを設定
        const fontStyle = this.buildFontStyle(settings, strap);
        this.ctx.font = fontStyle;
        // textBaselineを'top'に設定（drawText関数と一致させる）
        this.ctx.textBaseline = 'top';

        // 3. 各行の実際の幅を計測
        const lineWidths = lines.map(line => this.ctx.measureText(line).width);

        // 4. ストラップ内での配置を計算
        const positions = this.calculatePositions(
            lines,
            lineWidths,
            strap,
            settings.fontSize,
            settings.alignment,
            settings.horizontalAlignment,
            settings.verticalAlignment,
            settings.isVertical
        );

        // 5. TextLayoutオブジェクトを構築
        return {
            lines,
            positions,
            fontFamily: settings.fontFamily,
            fontSize: settings.fontSize,
            alignment: this.convertAlignmentToString(settings.alignment),
            isBold: settings.isBold,
            isItalic: settings.isItalic,
            isVertical: settings.isVertical
        };
    }

    /**
     * テキストを行に分割
     * @param text 分割するテキスト
     * @returns 行配列
     */
    private splitIntoLines(text: string): string[] {
        if (!text || text.trim().length === 0) {
            return [];
        }

        // 改行文字で分割（\r\n, \n の両方に対応）
        return text.split(/\r\n|\n/);
    }

    /**
     * 各行の位置を計算
     * @param lines 行配列
     * @param lineWidths 各行の幅（Canvas座標系、px）
     * @param strap ストラップ寸法
     * @param fontSize フォントサイズ（ポイント）
     * @param alignment 行揃え（複数行テキスト内での各行の揃え）
     * @param horizontalAlignment 水平方向の配置
     * @param verticalAlignment 垂直方向の配置
     * @param isVertical 縦書きフラグ
     * @returns 各行の位置情報
     */
    private calculatePositions(
        lines: string[],
        lineWidths: number[],
        strap: StrapDimensions,
        fontSize: number,
        alignment: TextAlignment,
        horizontalAlignment: HorizontalAlignment,
        verticalAlignment: VerticalAlignment,
        isVertical: boolean
    ): TextPosition[] {
        if (lines.length === 0) {
            return [];
        }

        // フォントサイズ変換：pt → Canvas px（ズームレベルに応じて自動調整）
        const scaledFontSize = fontSize * this.getPtToPx(strap);

        // 行の高さ（行間なし）
        const lineHeight = scaledFontSize * 1.0;

        // 最大行幅を計算（measureText().widthを使用）
        const maxLineWidth = lineWidths.length > 0 ? Math.max(...lineWidths) : 0;

        // バターの頭部分の高さ（18.1mm、ズームレベルに応じて自動調整）
        const mmToPx = this.getMmToPx(strap);
        const headHeightPx = Math.round(18.1 * mmToPx);

        // 彫刻可能領域の開始座標と終了座標（Canvas座標系）
        const engravableStartX = (strap.canvasWidth - strap.engravableWidthPx) / 2;
        const engravableStartY = headHeightPx + (strap.canvasHeight - headHeightPx - strap.engravableHeightPx) / 2;
        const engravableEndX = engravableStartX + strap.engravableWidthPx;
        const engravableEndY = engravableStartY + strap.engravableHeightPx;

        // まず、仮の位置を計算（行揃えのみを考慮）
        const tempPositions: TextPosition[] = [];
        for (let i = 0; i < lines.length; i++) {
            const y = i * lineHeight;

            // 行揃えに基づくX座標のオフセットを計算
            let lineOffsetX = 0;
            switch (alignment) {
                case TextAlignment.Left:
                    lineOffsetX = 0;
                    break;
                case TextAlignment.Center:
                    lineOffsetX = (maxLineWidth - lineWidths[i]) / 2;
                    break;
                case TextAlignment.Right:
                    lineOffsetX = maxLineWidth - lineWidths[i];
                    break;
            }

            tempPositions.push({
                x: lineOffsetX,
                y: y,
                text: lines[i]
            });
        }

        // 次に、仮位置での実際のグリフの境界を計算
        let actualMinX = Infinity, actualMinY = Infinity;
        let actualMaxX = -Infinity, actualMaxY = -Infinity;

        tempPositions.forEach(position => {
            if (position.text && position.text.length > 0) {
                const metrics = this.ctx.measureText(position.text);
                // actualBoundingBoxを使用して実際のグリフの境界を取得
                const actualLeft = metrics.actualBoundingBoxLeft;
                const actualRight = metrics.actualBoundingBoxRight;
                const actualAscent = metrics.actualBoundingBoxAscent;
                const actualDescent = metrics.actualBoundingBoxDescent;

                // 実際のグリフの左端と右端（textBaseline='top'を想定）
                const glyphLeft = position.x - actualLeft;
                const glyphRight = position.x + actualRight;
                const glyphTop = position.y - actualAscent;
                const glyphBottom = position.y + actualDescent;

                actualMinX = Math.min(actualMinX, glyphLeft);
                actualMinY = Math.min(actualMinY, glyphTop);
                actualMaxX = Math.max(actualMaxX, glyphRight);
                actualMaxY = Math.max(actualMaxY, glyphBottom);
            }
        });

        // 実際のテキストブロックのサイズ
        const actualWidth = actualMaxX - actualMinX;
        const actualHeight = actualMaxY - actualMinY;

        // 縦書きの場合は90度回転するため、回転後のサイズを使用
        // 90度回転すると、幅と高さが入れ替わる
        const rotatedWidth = isVertical ? actualHeight : actualWidth;
        const rotatedHeight = isVertical ? actualWidth : actualHeight;

        // 水平方向の配置を計算（回転後のサイズを使用）
        let targetX: number;
        switch (horizontalAlignment) {
            case HorizontalAlignment.Left:
                targetX = engravableStartX;
                break;
            case HorizontalAlignment.Center:
                targetX = engravableStartX + (strap.engravableWidthPx - rotatedWidth) / 2;
                break;
            case HorizontalAlignment.Right:
                targetX = engravableEndX - rotatedWidth;
                break;
        }

        // 垂直方向の配置を計算（回転後のサイズを使用）
        let targetY: number;
        switch (verticalAlignment) {
            case VerticalAlignment.Top:
                targetY = engravableStartY;
                break;
            case VerticalAlignment.Center:
                targetY = engravableStartY + (strap.engravableHeightPx - rotatedHeight) / 2;
                break;
            case VerticalAlignment.Bottom:
                targetY = engravableEndY - rotatedHeight;
                break;
        }

        // 実際のグリフの左上端が目標位置になるようにオフセットを計算
        const offsetX = targetX - actualMinX;
        const offsetY = targetY - actualMinY;

        // 各行の位置を調整
        const finalPositions: TextPosition[] = tempPositions.map(pos => ({
            x: pos.x + offsetX,
            y: pos.y + offsetY,
            text: pos.text
        }));

        return finalPositions;
    }

    /**
     * フォントスタイル文字列を構築
     * @param settings 彫刻設定
     * @returns フォントスタイル文字列
     */
    private buildFontStyle(settings: EngraveSettings, strap: StrapDimensions): string {
        const parts: string[] = [];

        // イタリック
        if (settings.isItalic) {
            parts.push('italic');
        }

        // 太字
        if (settings.isBold) {
            parts.push('bold');
        }

        // フォントサイズとファミリー（ズームレベルに応じて自動調整）
        const scaledFontSize = settings.fontSize * this.getPtToPx(strap);
        parts.push(`${scaledFontSize}px`);
        parts.push(settings.fontFamily || 'Arial');

        return parts.join(' ');
    }

    /**
     * TextAlignment列挙型を文字列に変換
     * @param alignment 行揃え
     * @returns "left", "center", "right"
     */
    private convertAlignmentToString(alignment: TextAlignment): string {
        switch (alignment) {
            case TextAlignment.Left:
                return 'left';
            case TextAlignment.Center:
                return 'center';
            case TextAlignment.Right:
                return 'right';
            default:
                return 'center';
        }
    }

    /**
     * テキストブロックの寸法（幅と高さ）を計算
     * @param lines テキスト行の配列
     * @param fontSize フォントサイズ（pt）
     * @param fontFamily フォントファミリー
     * @param isBold 太字フラグ
     * @param isItalic 斜体フラグ
     * @returns 幅と高さ（mm単位）
     */
    getTextBlockDimensions(
        lines: string[],
        fontSize: number,
        fontFamily: string,
        isBold: boolean,
        isItalic: boolean,
        strap: StrapDimensions
    ): TextBlockDimensions {
        if (!lines || lines.length === 0) {
            return { widthMm: 0, heightMm: 0 };
        }

        // フォントスタイルを設定
        const parts: string[] = [];
        if (isItalic) parts.push('italic');
        if (isBold) parts.push('bold');
        const scaledFontSize = fontSize * this.getPtToPx(strap);
        parts.push(`${scaledFontSize}px`);
        parts.push(fontFamily || 'Arial');
        this.ctx.font = parts.join(' ');

        // 各行の幅を計測
        const lineWidths = lines.map(line => this.ctx.measureText(line).width);
        const maxLineWidthPx = lineWidths.length > 0 ? Math.max(...lineWidths) : 0;

        // 行の高さ（xToolのリーディング=0相当：行間なし）
        const lineHeight = scaledFontSize * 1.0;
        const totalHeightPx = lines.length * lineHeight;

        // px → mm変換（ズームレベルに応じて自動調整）
        const mmToPx = this.getMmToPx(strap);
        const widthMm = maxLineWidthPx / mmToPx;
        const heightMm = totalHeightPx / mmToPx;

        return { widthMm, heightMm };
    }

    /**
     * レイアウトが彫刻可能範囲内かどうかを検証
     * @param layout テキストレイアウト
     * @param strap ストラップ寸法
     * @returns 検証結果
     */
    validateLayout(layout: TextLayout, strap: StrapDimensions): ValidationResult {
        const errors: string[] = [];
        const warnings: string[] = [];

        if (!layout.positions || layout.positions.length === 0) {
            return {
                isValid: true,
                errors,
                warnings
            };
        }

        // フォントスタイルを設定
        const fontStyle = this.buildFontStyle({
            text: '',
            fontFamily: layout.fontFamily,
            fontSize: layout.fontSize,
            alignment: TextAlignment.Center,
            isBold: layout.isBold,
            isItalic: layout.isItalic,
            isVertical: layout.isVertical,
            horizontalAlignment: HorizontalAlignment.Center,
            verticalAlignment: VerticalAlignment.Center
        }, strap);
        this.ctx.font = fontStyle;

        // 彫刻可能領域の境界（Canvas座標系）
        const mmToPx = this.getMmToPx(strap);
        const headHeightPx = Math.round(18.1 * mmToPx);
        const engravableStartX = (strap.canvasWidth - strap.engravableWidthPx) / 2;
        const engravableStartY = headHeightPx + (strap.canvasHeight - headHeightPx - strap.engravableHeightPx) / 2;
        const engravableEndX = engravableStartX + strap.engravableWidthPx;
        const engravableEndY = engravableStartY + strap.engravableHeightPx;

        // 浮動小数点の丸め誤差を許容するための微小値（0.5px = 0.05mm）
        const epsilon = 0.5;

        // 縦書きの場合は90度回転として扱う
        const rotationDegrees = layout.isVertical ? 90 : 0;
        const radians = (rotationDegrees * Math.PI) / 180.0;
        const hasRotation = layout.isVertical;

        if (!hasRotation) {
            // 回転なしの場合：actualBoundingBoxを使用して正確なグリフの境界をチェック
            for (let i = 0; i < layout.positions.length; i++) {
                const position = layout.positions[i];
                if (!position.text || position.text.length === 0) continue;

                const metrics = this.ctx.measureText(position.text);
                // actualBoundingBoxを使用して実際のグリフの境界を取得
                const actualLeft = metrics.actualBoundingBoxLeft;
                const actualRight = metrics.actualBoundingBoxRight;
                const actualAscent = metrics.actualBoundingBoxAscent;
                const actualDescent = metrics.actualBoundingBoxDescent;

                // 実際のグリフの境界
                const glyphLeft = position.x - actualLeft;
                const glyphRight = position.x + actualRight;
                const glyphTop = position.y - actualAscent;
                const glyphBottom = position.y + actualDescent;

                // X方向のチェック（エラー）
                if (glyphLeft < engravableStartX - epsilon) {
                    const overflowMm = (engravableStartX - glyphLeft) / mmToPx;
                    errors.push(`行${i + 1}が左側に${overflowMm.toFixed(1)}mm はみ出しています`);
                }

                if (glyphRight > engravableEndX + epsilon) {
                    const overflowMm = (glyphRight - engravableEndX) / mmToPx;
                    errors.push(`行${i + 1}が右側に${overflowMm.toFixed(1)}mm はみ出しています`);
                }

                // Y方向のチェック（エラー）
                if (glyphTop < engravableStartY - epsilon) {
                    const overflowMm = (engravableStartY - glyphTop) / mmToPx;
                    errors.push(`行${i + 1}が上側に${overflowMm.toFixed(1)}mm はみ出しています`);
                }

                if (glyphBottom > engravableEndY + epsilon) {
                    const overflowMm = (glyphBottom - engravableEndY) / mmToPx;
                    errors.push(`行${i + 1}が下側に${overflowMm.toFixed(1)}mm はみ出しています`);
                }
            }
        } else {
            // 回転ありの場合：テキストブロック全体の中心を回転中心として処理
            // 1. テキストブロック全体のバウンディングボックスを計算
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

            layout.positions.forEach(position => {
                if (position.text && position.text.length > 0) {
                    const metrics = this.ctx.measureText(position.text);
                    // actualBoundingBoxを使用して実際のグリフの境界を取得
                    const actualLeft = metrics.actualBoundingBoxLeft;
                    const actualRight = metrics.actualBoundingBoxRight;
                    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
                    const yOffset = -metrics.actualBoundingBoxAscent;

                    // 実際のグリフの左端と右端を使用
                    minX = Math.min(minX, position.x - actualLeft);
                    minY = Math.min(minY, position.y + yOffset);
                    maxX = Math.max(maxX, position.x + actualRight);
                    maxY = Math.max(maxY, position.y + yOffset + textHeight);
                }
            });

            // 2. テキストブロックの中心点を計算
            const textCenterX = (minX + maxX) / 2;
            const textCenterY = (minY + maxY) / 2;

            // 3. まず、オフセット計算なしで回転後のAABBを計算
            let aabbMinX = Infinity, aabbMinY = Infinity;
            let aabbMaxX = -Infinity, aabbMaxY = -Infinity;

            const cos = Math.cos(radians);
            const sin = Math.sin(radians);

            layout.positions.forEach(position => {
                if (position.text && position.text.length > 0) {
                    const metrics = this.ctx.measureText(position.text);
                    // actualBoundingBoxを使用して実際のグリフの境界を取得
                    const actualLeft = metrics.actualBoundingBoxLeft;
                    const actualRight = metrics.actualBoundingBoxRight;
                    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
                    const yOffset = -metrics.actualBoundingBoxAscent;

                    // バウンディングボックスの4つの角（回転前）
                    // 実際のグリフの左端と右端を使用
                    const boxLeft = position.x - actualLeft;
                    const boxRight = position.x + actualRight;
                    const boxY = position.y + yOffset;
                    const corners = [
                        [boxLeft, boxY],
                        [boxRight, boxY],
                        [boxRight, boxY + textHeight],
                        [boxLeft, boxY + textHeight]
                    ];

                    // 各角を回転中心まわりに回転変換
                    corners.forEach(([x, y]) => {
                        const dx = x - textCenterX;
                        const dy = y - textCenterY;

                        const rotatedX = dx * cos - dy * sin + textCenterX;
                        const rotatedY = dx * sin + dy * cos + textCenterY;

                        aabbMinX = Math.min(aabbMinX, rotatedX);
                        aabbMinY = Math.min(aabbMinY, rotatedY);
                        aabbMaxX = Math.max(aabbMaxX, rotatedX);
                        aabbMaxY = Math.max(aabbMaxY, rotatedY);
                    });
                }
            });

            // 4. ユーザー指定の目標位置（回転なしの状態での左上端）
            const targetX = minX;
            const targetY = minY;

            // 5. 回転後のAABBの左上端が目標位置になるようにオフセットを計算
            const offsetX = targetX - aabbMinX;
            const offsetY = targetY - aabbMinY;

            // 6. オフセットを適用した最終的なAABB（drawTextと同じロジック）
            const finalMinX = aabbMinX + offsetX;
            const finalMinY = aabbMinY + offsetY;
            const finalMaxX = aabbMaxX + offsetX;
            const finalMaxY = aabbMaxY + offsetY;

            // バウンディングボックスが彫刻可能領域内にあるかチェック（エラー）
            if (finalMinX < engravableStartX - epsilon) {
                const overflowMm = (engravableStartX - finalMinX) / mmToPx;
                errors.push(`テキストが左側に${overflowMm.toFixed(1)}mm はみ出しています`);
            }

            if (finalMaxX > engravableEndX + epsilon) {
                const overflowMm = (finalMaxX - engravableEndX) / mmToPx;
                errors.push(`テキストが右側に${overflowMm.toFixed(1)}mm はみ出しています`);
            }

            if (finalMinY < engravableStartY - epsilon) {
                const overflowMm = (engravableStartY - finalMinY) / mmToPx;
                errors.push(`テキストが上側に${overflowMm.toFixed(1)}mm はみ出しています`);
            }

            if (finalMaxY > engravableEndY + epsilon) {
                const overflowMm = (finalMaxY - engravableEndY) / mmToPx;
                errors.push(`テキストが下側に${overflowMm.toFixed(1)}mm はみ出しています`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }
}
